import { LucideIcon } from 'lucide-react';
import React from 'react';

import { Button, Card, CardContent } from '@/components/design-system';
import {
  COLOR_SCALE,
  FLEX,
  FLEX_ALIGN,
  FLEX_JUSTIFY,
  FONT_WEIGHTS,
  MARGIN_BOTTOM,
  MARGIN_RIGHT,
  MARGIN_X,
  PADDING_X,
  PADDING_Y,
  RADIUS_SCALE,
  SHADOW_SCALE,
  SIZE_SCALE,
  TEXT_ALIGNMENT,
  TYPE_SCALE,
} from '@/components/design-system/tokens';

export interface EmptyStateProps {
  /**
   * Icon to display in the empty state
   */
  icon: LucideIcon;
  /**
   * Title text
   */
  title: string;
  /**
   * Description text
   */
  description: string;
  /**
   * Optional action button
   */
  action?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
  };
  /**
   * Size variant for the icon container
   * @default 'md'
   */
  iconSize?: 'sm' | 'md' | 'lg';
  /**
   * Whether to show border on card
   * @default false
   */
  showBorder?: boolean;
  /**
   * Custom className for the card
   */
  className?: string;
}

/**
 * Standardized empty state component with consistent design pattern
 * Used across admin and public pages to display empty data states
 */
function getIconContainerSize(iconSize: 'sm' | 'md' | 'lg'): string {
  const sizes = {
    sm: `${SIZE_SCALE.w16} ${SIZE_SCALE['16']}`,
    md: `${SIZE_SCALE.w24} ${SIZE_SCALE['24']}`,
    lg: `${SIZE_SCALE.w28} ${SIZE_SCALE['28']}`,
  };
  return sizes[iconSize];
}

function getIconSize(iconSize: 'sm' | 'md' | 'lg'): string {
  const sizes = {
    sm: `${SIZE_SCALE.w8} ${SIZE_SCALE['8']}`,
    md: `${SIZE_SCALE.w12} ${SIZE_SCALE['12']}`,
    lg: `${SIZE_SCALE['14']}`,
  };
  return sizes[iconSize];
}

function getTitleSize(iconSize: 'sm' | 'md' | 'lg'): string {
  const sizes = {
    sm: TYPE_SCALE.lg,
    md: TYPE_SCALE.xl,
    lg: TYPE_SCALE['2xl'],
  };
  return sizes[iconSize];
}

function getPaddingSize(iconSize: 'sm' | 'md' | 'lg'): string {
  const sizes = {
    sm: PADDING_Y['12'],
    md: PADDING_Y['16'],
    lg: PADDING_Y['24'],
  };
  return sizes[iconSize];
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  iconSize = 'md',
  showBorder = false,
  className = '',
}: EmptyStateProps) {
  const iconContainerSize = getIconContainerSize(iconSize);
  const iconSizeClass = getIconSize(iconSize);
  const titleSize = getTitleSize(iconSize);
  const paddingSize = getPaddingSize(iconSize);

  return (
    <Card
      className={`${showBorder ? `border ${COLOR_SCALE['admin-border']}` : 'border-0'} ${SHADOW_SCALE.sm} ${RADIUS_SCALE['2xl']} overflow-hidden bg-gradient-to-br from-white to-gray-50 ${className}`}
    >
      <CardContent className={`${paddingSize} ${TEXT_ALIGNMENT.center}`}>
        <div className={`relative ${iconContainerSize} ${MARGIN_X.auto} ${MARGIN_BOTTOM['4']}`}>
          <div
            className={`absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 ${RADIUS_SCALE['2xl']} opacity-10 blur-xl`}
          ></div>
          <div
            className={`relative ${iconContainerSize} bg-gradient-to-br from-blue-500 to-indigo-600 ${RADIUS_SCALE['2xl']} ${FLEX.flex} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.center} ${SHADOW_SCALE.lg} shadow-blue-200`}
          >
            <Icon className={`${iconSizeClass} text-white`} />
          </div>
        </div>
        <h3
          className={`${titleSize} ${FONT_WEIGHTS.semibold} ${COLOR_SCALE.foreground} ${MARGIN_BOTTOM['2']}`}
        >
          {title}
        </h3>
        <p
          className={`${TYPE_SCALE.sm} ${COLOR_SCALE['foreground-muted']} ${SIZE_SCALE.maxWmd} mx-auto ${action ? MARGIN_BOTTOM['4'] : ''}`}
        >
          {description}
        </p>
        {action && (
          <Button
            onClick={action.onClick}
            className={`${RADIUS_SCALE.xl} ${PADDING_X['6']} ${PADDING_Y['3']} ${FONT_WEIGHTS.semibold} shadow-sm`}
          >
            {action.icon && (
              <action.icon className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']} ${MARGIN_RIGHT['2']}`} />
            )}
            {action.label}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
